import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Toolbar,
  IconButton,
  Container,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";

export default function CustomerPurchase() {
  const navigate = useNavigate();
  const { id } = useParams();

  // read logged-in user from localStorage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const civil_id = user?.civil_id;

  // navigation helpers
  const backward = () => navigate("/customerHome");
  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    navigate("/");
  };

  // state
  const [sellings, setSellings] = useState([]);
  const [data, setData] = useState([]);

  // fetch sellings & payments
  const fetchSellings = useCallback(async () => {
    try {
      const resp1 = await axios.get(
        `https://app.smartco.live/selling/getOneSellingID/${id}`
      );
      setSellings(resp1.data);

      const { civilID, emiNumber, deviceName } = resp1.data[0];
      const resp2 = await axios.post(
        "https://app.smartco.live/payment/getOnePayment",
        { civilID, emiNumber }
      );
      setData(resp2.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  }, [id]);

  useEffect(() => {
    fetchSellings();
  }, [fetchSellings]);

  // download individual invoice (unchanged)
  const downloadPDF = (rowData) => {
    fetch("https://app.smartco.live/convertToCustomerPaymentInvoicePDF", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(rowData),
    })
      .then((r) => {
        if (!r.ok) throw new Error("Error converting to PDF");
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "output.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((e) => alert(e));
  };

  // open overall bill URL in new tab (or WebView)
  const openOverallPDF = () => {
    if (!civil_id) return alert("User not logged in");
    const params = new URLSearchParams({ id, civil_id });
    const url = `https://app.smartco.live/convertToOverAllPaymentInvoicePDF?${params}`;
    window.open(url, "_blank");
  };

  return (
    <Container>
      <Toolbar sx={{ pr: 3, mt: 2 }}>
        <Box sx={{ display: "flex", width: "100%", alignItems: "center" }}>
          <IconButton onClick={backward} sx={{ mr: "auto" }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              background: "linear-gradient(90deg, #C63DE7, #752888)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            SMARTCO
          </Typography>
          <IconButton onClick={handleLogout} sx={{ ml: "auto" }}>
            <LogoutIcon />
          </IconButton>
        </Box>
      </Toolbar>

      {sellings.map((selling) => (
        <Box key={selling._id} sx={{ my: 2, mx: "auto", maxWidth: 600 }}>
          <Typography variant="h5" align="center" sx={{ mb: 2 }}>
            {selling.deviceName}
          </Typography>

          {/* Payment Plan Table */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Payment Plan
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selling.customArray?.map((item) => (
                      <TableRow key={item._id}>
                        <TableCell>{item.date}</TableCell>
                        <TableCell>{item.price}/=</TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            color={item.status === "paid" ? "success" : "error"}
                            size="small"
                          >
                            {item.status.toUpperCase()}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Summary Boxes */}
          <Box sx={{ mb: 2, p: 2, bgcolor: "#e2cae7", borderRadius: 1 }}>
            <Typography align="right">
              Device price: {selling.price}/=
            </Typography>
          </Box>
          <Box sx={{ mb: 2, p: 2, bgcolor: "#e2cae7", borderRadius: 1 }}>
            <Typography align="right">
              Advance: {selling.advance}/=
            </Typography>
          </Box>
          <Box sx={{ mb: 2, p: 2, bgcolor: "#a53db5", borderRadius: 1 }}>
            <Typography align="right" sx={{ color: "#fff", fontWeight: "bold" }}>
              Remaining Balance: {selling.balance}/=
            </Typography>
          </Box>

          {/* Generate Overall Bill */}
          <Box sx={{ mb: 4, p: 2, bgcolor: "#a53db5", borderRadius: 1 }}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                  Generate Overall Bill
                </Typography>
              </Grid>
              <Grid item>
                <Button variant="contained" onClick={openOverallPDF}>
                  GENERATE BILL
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ))}

      {/* Payment History */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Payment History
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Device Name</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{item.deviceName}</TableCell>
                    <TableCell>{item.price}/=</TableCell>
                    <TableCell>{item.date}</TableCell>
                    <TableCell>
                      <Button variant="contained" size="small" onClick={() => downloadPDF(item)}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
}
